import { type MutationOptions, useMutation } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { type InvoiceSendingSettings } from './useQueryCompanyInvoiceSendingSettings';

type UpdateInvoiceSendingSettingsParams = {
  companyId: UUID;
  invoiceSendingSettings: Partial<InvoiceSendingSettings>;
};

/**
 * Set different company invoice sending settings.
 *
 * @param {string} companyId - The ID of the company to update invoice sending settings.
 * @param {Object} invoiceSendingSettings - An object containing the settings.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/set_company_invoice_settings_company__id__invoice_sending_settings_put
 */
export const putUpdateInvoiceSendingSettings = async ({
  companyId,
  invoiceSendingSettings,
}: UpdateInvoiceSendingSettingsParams) => {
  const response = await vestigasApi
    .put(ENDPOINT.COMPANY.UPDATE_INVOICE_SENDING_SETTINGS(companyId), {
      json: invoiceSendingSettings,
    })
    .json();

  return response;
};

/**
 * Custom hook for saving invoice sending settings of a given company.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateInvoiceSendingSettings = (
  options?: MutationOptions<unknown, Error, UpdateInvoiceSendingSettingsParams>,
) =>
  useMutation<unknown, Error, UpdateInvoiceSendingSettingsParams>({
    mutationFn: async ({
      invoiceSendingSettings,
      companyId,
    }: UpdateInvoiceSendingSettingsParams) =>
      putUpdateInvoiceSendingSettings({ companyId, invoiceSendingSettings }),
    ...options,
  });
